import React from 'react';
import useSWR from 'swr';
import EntityContentType from '@/constants/EntityContentType';
import FeedEntityType from '@/constants/FeedEntityType';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { createApiRequest, fetcher } from '@/utils/services';
import './_typedef';

// Maps an entity type to its API root, which is generally the plural version
// of that type.
const entityTypeMap = {
  ...FeedEntityType,
  board: 'boards',
};

const NEEDS_ORG_ID = [EntityContentType.COMMS];

/**
 * Uses the SWR library to fetch data "entities". SWR manages request data
 * caching, de-duplication, and interval revalidation and retries.
 * See: https://swr.vercel.app/
 * @param {string?} contentId - ID of the content to be fetched.
 * @param {string?} contentType - the content type we will be getting, generally a singular noun
 * @param {Object?} swrOptions - generally optional. See: https://swr.vercel.app/docs/options
 * @returns {SwrEntity}
 */
const useEntity = (contentId, type, swrOptions = {}) => {
  const ownerOrgId = useOwnerOrgId();

  // TODO(jon): This is kind of janky. Depending on context, we may be getting a
  // singular or plural "type", e.g. "donation" vs "donations".
  const contentType = entityTypeMap[type] || type;
  let key = contentId && contentType ? `${contentType}/${contentId}` : null;

  if (key && NEEDS_ORG_ID.includes(contentType)) {
    key = `${key}?owner_org_id=${ownerOrgId}`;
  }

  /**
   * API responses will include the entity data under a key corresponding to its
   * type, but it will sometimes include additional information under other
   * keys. We need to flatten this object so the primary entity keys are sibling
   * to the additional information keys
   */
  const mapResponse = (response) => {
    const result = {
      // HACK: until the feed is updated to return content types that match up
      // with the actual content, we need to use the hack of directly accessing
      // response.campaign. See entityDetailsConfig.js for more context.
      ...(response[contentType] ||
        response[type] ||
        response.campaign ||
        response.email),
      ...response,
    };
    delete result[contentType];
    delete result[type];
    return result;
  };

  const [isBusy, setIsBusy] = React.useState(false);

  // There is an issue with isBusy not updating the mutateEntity and remove
  // functions. The first time they are invoked, isBusy gets set to true and
  // is eventually set to false when the AJAX request has completed. However,
  // the useCallback for those functions does not update accordingly. This
  // results in isBusy always being true after that first invocation.
  // Subsequent invocations will always return early. But creating this ref,
  // we can use that instead to determine if the request is busy or not.
  // See more additional context: https://github.com/instilus/client/pull/1139
  const isBusyRef = React.useRef(isBusy);

  React.useEffect(() => {
    isBusyRef.current = isBusy;
  }, [isBusy]);

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    key,
    async (path) => mapResponse(await fetcher(path)),
    swrOptions,
  );

  const mutateEntity = React.useCallback(
    async (updates) => {
      if (isBusyRef.current) return;
      setIsBusy(true);
      try {
        // Merge changes in to local state for a quick UI update.
        mutate((currentData) => ({ ...currentData, ...updates }), {
          revalidate: false,
        });
        // Send updates to the backend. PUT requests return updated data.
        const res = await createApiRequest({
          data: updates,
          method: 'put',
          path: key,
          throwErrors: true,
        });
        // Update local state with the official backend response. Since we have
        // updated data from the `PUT`, we again don't need to revalidate.
        mutate(mapResponse(res.data), { revalidate: false });
      } finally {
        setIsBusy(false);
      }
    },
    [key, mutate],
  );

  const remove = React.useCallback(async () => {
    if (isBusyRef.current) return;
    setIsBusy(true);
    await createApiRequest({
      method: 'delete',
      path: key,
      throwErrors: true,
    });
    mutate(undefined);
    setIsBusy(false);
  }, [key, mutate]);

  return {
    data,
    error,
    isBusy,
    isLoading,
    isValidating,
    mutate: mutateEntity,
    remove,
  };
};

export default useEntity;
